import { useCallback, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { Button } from '@/atoms/Button'
import { ParagraphSM, TitleMD } from '@/atoms/Text'
import { useTranslate } from '@/utils/translate/translate-client'
import { OptionGroup } from '../../components/OptionGroup'
import { Page } from '../../components/Page'
import { PageSection } from '../../components/PageSection'
import {
  TorchApplicationStatus,
  TorchApplicationTypes,
  useTorchApplication,
} from '../../components/TorchApplicationContext'
import { GET_UPLOADED_FILES } from '../../queries'
import { useUserGuildProjectQuery } from '../AssociatedProject/hooks/useUserGuildProjectQuery'
import { FileBox } from './FileBox'
import { MultiUpload } from './MultiUpload'
import { SingleUpload } from './SingleUpload'
import { ThumbnailUploadCheckboxes } from './ThumbnailUploadCheckboxes'
import { TorchUploadCheckboxes } from './TorchUploadCheckboxes'
import { UploadAcknowledgement } from './UploadAcknowledgement'
import { UploadBox } from './UploadBox'

export interface UploadedFile {
  fileName: string
  type: string
  size: number
  status: 'APPROVED' | 'PENDING' | 'REJECTED'
}

interface GetUploadNextPageProps {
  isFullFilm: boolean
  isEpisode: boolean
  hasProjects: boolean
}

const getUploadNextPage = ({ isFullFilm, isEpisode, hasProjects }: GetUploadNextPageProps) => {
  if (isFullFilm) return 4

  if (isEpisode && !hasProjects) return 6

  return 5
}

export const Upload = () => {
  const { t } = useTranslate('guild-create')
  const { id, loading, type, data, isDraft, status, setPage, updateStatus, updateData } = useTorchApplication()
  const { data: uploadsRes, refetch: loadFiles } = useQuery(GET_UPLOADED_FILES, {
    variables: { id },
    errorPolicy: 'all',
  })

  const { data: projectData } = useUserGuildProjectQuery()

  const { isFullFilm, isEpisode, isCompletedMovie } = useMemo(() => {
    return {
      isFullFilm: type === TorchApplicationTypes.FullFilm,
      isEpisode: type === TorchApplicationTypes.CompletedEpisode,
      isCompletedMovie: type === TorchApplicationTypes.CompletedMovie,
    }
  }, [type])

  const priorPage = isFullFilm ? 2 : 3
  const nextPage = getUploadNextPage({
    isFullFilm,
    isEpisode,
    hasProjects: !!projectData?.guildUserProjects?.length,
  })

  const handleNext = useCallback(() => {
    if (status === TorchApplicationStatus.Incomplete || data?.paymentDate) {
      updateStatus(TorchApplicationStatus.PendingReview)
    }
    setPage(nextPage)
  }, [status, data, setPage, nextPage, updateStatus])

  const handleAllowShare = useCallback(
    (value: string[]) => {
      updateData({ allowShares: value[0] === 'yes' })
    },
    [updateData],
  )

  const { videoFile, thumbnailFile, hasBothUploads, videos } = useMemo(() => {
    const uploads: UploadedFile[] = uploadsRes?.torchApplication?.uploads || []

    const sortedUploads = uploads.reduce(
      (acc, file) => {
        if (file.type.includes('image')) {
          return { ...acc, thumbnailFile: file }
        }
        if (file.type.includes('video')) {
          acc.videos.push(file)
          return { ...acc, videoFile: file }
        }

        return acc
      },
      { videoFile: null, thumbnailFile: null, videos: [] } as {
        videoFile: UploadedFile | null
        thumbnailFile: UploadedFile | null
        videos: UploadedFile[]
      },
    )

    return {
      ...sortedUploads,
      hasBothUploads: !!sortedUploads.videoFile && !!sortedUploads.thumbnailFile,
    }
  }, [uploadsRes?.torchApplication?.uploads])

  const allowShareValue = useMemo(() => {
    if (typeof data?.allowShares === 'boolean') return data.allowShares ? ['yes'] : ['no']
    return []
  }, [data?.allowShares])

  const torchAcknowledgement =
    hasBothUploads || data?.uploadAcknowledgements?.includes('torch') || data?.uploadAcknowledgements?.includes('movie')
  const thumbnailAcknowledgement = hasBothUploads || data?.uploadAcknowledgements?.includes('thumbnail')

  const isIncomplete = status === TorchApplicationStatus.Incomplete
  const canContinue =
    hasBothUploads &&
    torchAcknowledgement &&
    (thumbnailAcknowledgement || isFullFilm) &&
    (isFullFilm || typeof data?.allowShares === 'boolean')
  const canUpload = isDraft || isIncomplete

  if (loading) return null

  return (
    <Page
      disabled={!canContinue}
      onBack={() => setPage(priorPage)}
      onNext={isIncomplete ? undefined : () => setPage(nextPage)}
    >
      <PageSection>
        <UploadAcknowledgement />
        <TitleMD weight="bold">
          {isFullFilm
            ? t('submitYourFullLengthFilm', 'Submit Your Full-Length Film')
            : t('submitYourProject', 'Submit Your Project')}
        </TitleMD>
        {!isFullFilm && (
          <ParagraphSM color="gray-600" weight="normal">
            {t(
              'torchDetailsAcknowledgementDescriptionConceptUpdated',
              'A "Torch" is a representative sample of what the final production will look like and may take the form of a concept video or a completed episode. Torches are recommended to be 20 minutes or less (but may be longer). Torches may not be trailers or sizzle reels, but should be a continuous scene (or multiple scenes put together) that give the average viewer a good understanding of the plot, tone, and pace of the story. We currently do not allow any credits, company logos, names, or pitches from the filmmaker to be included with a Torch. ',
            )}
          </ParagraphSM>
        )}
        <TorchUploadCheckboxes disabled={!canUpload} hasVideo={!!videoFile} />
      </PageSection>
      <PageSection>
        <TitleMD weight="bold">
          {isCompletedMovie ? t('uploadVideos', 'Upload Videos') : t('uploadVideo', 'Upload Video')}
        </TitleMD>
        {isCompletedMovie ? (
          <MultiUpload
            files={videos}
            loadFiles={loadFiles}
            canUpload={canUpload}
            torchAcknowledgement={torchAcknowledgement ?? false}
          />
        ) : (
          <SingleUpload
            file={videoFile}
            loadFiles={loadFiles}
            canUpload={canUpload}
            torchAcknowledgement={torchAcknowledgement ?? false}
            isFullFilm={isFullFilm}
          />
        )}
      </PageSection>

      <PageSection>
        <TitleMD weight="bold">
          {t('uploadALandscapeThumbnail169Preferred', 'Upload a Landscape Thumbnail (16:9 Preferred)')}
        </TitleMD>
        <ThumbnailUploadCheckboxes disabled={!canUpload} isThumbnailUploaded={!!thumbnailFile || isFullFilm} />
        {thumbnailFile ? (
          <FileBox file={thumbnailFile} loadFiles={loadFiles} disabled={!canUpload} />
        ) : (
          <UploadBox
            acknowledged={thumbnailAcknowledgement}
            acceptedFileType="image"
            loadFiles={loadFiles}
            maxSize={40_000_000}
          />
        )}
      </PageSection>
      {!isFullFilm && (
        <PageSection>
          <OptionGroup
            label={t(
              'torchAskFilmakerForSharing',
              'Allow Guild Voters to invite others to watch and vote on your submission?',
            )}
            required
            limit={1}
            options={[
              { label: t('yes', 'Yes'), value: 'yes' },
              { label: t('no', 'No'), value: 'no' },
            ]}
            name="allowTorchShare"
            onChange={handleAllowShare}
            value={allowShareValue}
          />
        </PageSection>
      )}
      <Button variant="oxide" disabled={!canContinue} onClick={handleNext} className="w-72">
        {isIncomplete ? t('resubmitApplication', 'Resubmit Application') : t('continue', 'Continue')}
      </Button>
    </Page>
  )
}
